import {
  CheckCircle,
  Close,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  HourglassEmpty,
  WarningAmber,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";

export type UploadJobStatus = "queued" | "processing" | "done" | "error";

export interface UploadJob {
  id: string;
  name: string;
  status: UploadJobStatus;
  receipts: number;
  flagged: number;
  skippedZero: number;
  error?: string;
}

const isFinished = (job: UploadJob) => job.status === "done" || job.status === "error";

const StatusIcon: FC<{ job: UploadJob }> = ({ job }) => {
  if (job.status === "processing") return <CircularProgress size={18} />;
  if (job.status === "queued") return <HourglassEmpty fontSize="small" color="disabled" />;
  if (job.status === "error") return <ErrorOutline fontSize="small" color="error" />;
  if (job.flagged > 0 || job.receipts === 0) {
    return <WarningAmber fontSize="small" color="warning" />;
  }
  return <CheckCircle fontSize="small" color="success" />;
};

const describe = (job: UploadJob) => {
  if (job.status === "queued") return "Waiting…";
  if (job.status === "processing") return "Reading receipts…";
  if (job.status === "error") return job.error || "Failed";
  const parts = [`${job.receipts} receipt${job.receipts === 1 ? "" : "s"}`];
  if (job.flagged) parts.push(`${job.flagged} to check`);
  if (job.skippedZero) parts.push(`${job.skippedZero} ignored (amount 0)`);
  return parts.join(" · ");
};

/**
 * Fixed bottom-right panel that follows a batch of receipt uploads file by
 * file, so a 20-photo import shows where it is instead of one long spinner.
 */
export const UploadProgressWidget: FC<{
  jobs: UploadJob[];
  onClose: () => void;
}> = ({ jobs, onClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  if (jobs.length === 0) return null;

  const finished = jobs.filter(isFinished).length;
  const running = finished < jobs.length;
  const failed = jobs.filter((job) => job.status === "error").length;
  const receipts = jobs.reduce((sum, job) => sum + job.receipts, 0);

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        right: 16,
        bottom: 16,
        width: 360,
        maxWidth: "calc(100vw - 32px)",
        zIndex: (theme) => theme.zIndex.snackbar - 1,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1, gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {running
              ? `Importing ${finished} / ${jobs.length} file${jobs.length === 1 ? "" : "s"}`
              : `Import finished · ${receipts} receipt${receipts === 1 ? "" : "s"}`}
          </Typography>
          {!running && failed > 0 ? (
            <Typography variant="caption" color="error">
              {failed} file{failed === 1 ? "" : "s"} failed
            </Typography>
          ) : null}
        </Box>
        <IconButton size="small" onClick={() => setCollapsed((value) => !value)}>
          {collapsed ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
        <Tooltip title={running ? "Wait for the import to finish" : "Close"}>
          <span>
            <IconButton size="small" onClick={onClose} disabled={running}>
              <Close fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={(finished / jobs.length) * 100}
        color={failed ? "warning" : "primary"}
      />
      {!collapsed ? (
        <Box sx={{ maxHeight: 280, overflowY: "auto", py: 0.5 }}>
          {jobs.map((job) => (
            <Stack
              key={job.id}
              direction="row"
              alignItems="center"
              sx={{ px: 2, py: 0.75, gap: 1.5 }}
            >
              <Box sx={{ width: 20, display: "flex", justifyContent: "center" }}>
                <StatusIcon job={job} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap title={job.name}>
                  {job.name}
                </Typography>
                <Typography
                  variant="caption"
                  color={job.status === "error" ? "error" : "text.secondary"}
                  sx={{ display: "block", wordBreak: "break-word" }}
                >
                  {describe(job)}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      ) : null}
    </Paper>
  );
};
